import type { HelpSectionsMap } from './types';

export const deHelpSections: HelpSectionsMap = {
  start: {
    title: 'Erste Schritte',
    intro: 'Eine kurze Anleitung für den Einstieg in WERSTACK. Folgen Sie den Schritten der Reihe nach, um sich schneller zurechtzufinden.',
    blocks: [
      {
        title: 'Schritt 1. Workspace auswählen',
        text: 'Ein Workspace ist Ihr Projekt mit eigenen Dateien, Einstellungen und Teammitgliedern.',
        items: [
          'Öffnen Sie in der linken Seitenleiste „Meine“ oder „Geteilt“ und wählen Sie einen Workspace.',
          'Dateien und Ordner wechseln automatisch.',
          '„Erstellen“ oben in der Seitenleiste — neuer persönlicher Workspace (sofern unter dem Limit).',
          'Jeder Workspace kann eigene Sprache, Theme und Akzentfarbe haben.',
        ],
        where: 'Linke Seitenleiste → „Meine“ / „Geteilt“. Bei eingeklappter Leiste fahren Sie mit der Maus über das Abschnittssymbol für die Liste.',
      },
      {
        title: 'Schritt 2. Erste Dateien hochladen',
        items: [
          'Klicken Sie in der Galerie-Symbolleiste auf „Bilder hochladen“.',
          'Ctrl+Shift+V — Screenshot aus der Zwischenablage in den aktuellen Ordner einfügen.',
          'Bis zu 1000 Dateien gleichzeitig (JPEG, PNG, WebP; jeweils bis zu 10 MB).',
          'Unten erscheint ein Fortschrittsbalken — Sie können ihn schließen oder abbrechen.',
        ],
        where: 'Startbildschirm → Schaltfläche „Bilder hochladen“ oben rechts.',
      },
      {
        title: 'Schritt 3. Ordner anlegen',
        items: [
          'Klicken Sie auf „Ordner erstellen“, geben Sie einen Namen ein und bestätigen Sie.',
          'Öffnen Sie einen Ordner per Doppelklick (einmal tippen auf Touch-Geräten).',
          'Auf Workspace-Ebene zeigt die Symbolleiste links den Workspace-Namen.',
          'In einem Unterordner — Brotkrumen mit Home-Symbol für die Rückkehr zur Wurzel.',
        ],
        where: 'Startbildschirm → „Ordner erstellen“ neben dem Upload.',
      },
      {
        title: 'Seitenleiste',
        items: [
          'WERSTACK-Logo und Workspace-Listen oben.',
          '„Erstellen“ — neuer Workspace.',
          '„Daten“ — Tabellen: „Alle“ öffnet den Katalog; Listen darunter zum schnellen Öffnen.',
          '„Boards“ — Kanban über Tabellen.',
          '„Workflows“ — Automationsgraphen im aktuellen Workspace.',
          '„Galerien“ — Katalog öffentlicher Galerien.',
          'Rand-Schaltfläche — Seitenleiste ein- oder ausklappen.',
        ],
        where: 'Linke Spalte des Hauptbildschirms.',
      },
      {
        title: 'Schaltflächen in der oberen Leiste',
        items: [
          'Glocke — Teameinladungen und Kommentar-Benachrichtigungen.',
          'Fragezeichen — dieses Hilfe-Panel (getrennt vom Profil).',
          'Initialen — Abmelden.',
        ],
        where: 'Obere rechte Ecke des Bildschirms.',
      },
      {
        title: 'Rollen und Berechtigungen',
        text: 'Nicht jede Aktion steht jedem Benutzer zur Verfügung.',
        items: [
          'Owner und Admin — voller Zugriff, Verwaltung von Mitgliedern und Galerien.',
          'Editor — Dateien ansehen und bearbeiten, keine Teamverwaltung.',
          'Viewer — nur Ansehen, kein Upload oder Löschen.',
        ],
        tip: 'Wenn eine Schaltfläche deaktiviert ist oder ein Menüpunkt fehlt, haben Sie möglicherweise die Viewer-Rolle.',
      },
    ],
  },
  gallery: {
    title: 'Galerie: Dateien und Ordner',
    intro: 'Der Hauptbildschirm, auf dem Ihre Bilder gespeichert und organisiert werden.',
    blocks: [
      {
        title: 'Symbolleiste',
        items: [
          'Links — Workspace-Name (auf Wurzelebene), Brotkrumen (in einem Unterordner) oder aktiver Such-/Filterhinweis.',
          'Rechts — Upload, Ordner erstellen, Suche, Tag-Filter, Sortierung, Raster-/Listenansicht, Papierkorb.',
        ],
        where: 'Leiste unter der oberen App-Kopfzeile.',
      },
      {
        title: 'Ansichtsmodi',
        items: [
          'Raster — Karten mit Vorschau, Likes und Kommentar-Indikator.',
          'Liste — Tabelle: Name, Datum, Typ, Größe; praktisch für Mehrfachauswahl.',
        ],
        where: 'Ansichts-Umschalter (Raster- und Listen-Symbole) rechts in der Symbolleiste.',
      },
      {
        title: 'Dateisuche',
        items: [
          'Klicken Sie auf das Lupen-Symbol und geben Sie mindestens 2 Zeichen ein.',
          'Die Suche umfasst Dateiname, Tags und Kommentartext.',
          'Suche beenden — „Zurücksetzen“ klicken oder das Feld leeren.',
        ],
        where: 'Lupen-Symbol in der Symbolleiste. Sortierung und Upload sind während der Suche ausgeblendet.',
      },
      {
        title: 'Tag-Filter',
        items: [
          'Klicken Sie auf das Tag-Symbol und wählen Sie einen oder mehrere Tags.',
          'Ausgewählte Tags erscheinen links als Chips — mit × entfernen.',
          'Bei aktiven Tags erscheint „Tag-Galerie erstellen“ (siehe Öffentliche Galerien).',
        ],
        where: 'Tag-Symbol in der Symbolleiste.',
      },
      {
        title: 'Sortierung',
        items: [
          'Nach Upload-Datum (neueste oder älteste zuerst).',
          'Nach Name (A–Z oder Z–A).',
          'Nach Dateigröße.',
          'Nach Anzahl der Likes.',
        ],
        where: 'Schieberegler-Symbol in der Symbolleiste. Nicht verfügbar im Papierkorb, bei Suche oder Tag-Filter.',
      },
      {
        title: 'Likes',
        items: [
          'Klicken Sie auf das Herz auf einer Karte, um zu liken oder den Like zu entfernen.',
          'Liken öffnet die Datei nicht — klicken Sie auf Vorschau oder Name zum Öffnen.',
        ],
      },
      {
        title: 'Seitennummerierung',
        text: 'Bei vielen Dateien nutzen Sie das Panel unten, um Seiten und Einträge pro Seite zu ändern.',
        where: 'Seitenleiste unten am Bildschirm.',
      },
    ],
  },
  selection: {
    title: 'Auswahl und Aktionen',
    intro: 'So wählen Sie mehrere Dateien und Ordner aus und was Sie damit tun können.',
    blocks: [
      {
        title: 'Auswahl mit der Maus',
        items: [
          'Klick auf ein Element — zur Auswahl hinzufügen oder entfernen.',
          'Shift + Klick — Bereich ab dem zuletzt ausgewählten Element markieren.',
          'Rahmen auf leerem Bereich ziehen — alles darin auswählen.',
          'Ctrl (Cmd auf Mac) + Rahmen — zur aktuellen Auswahl hinzufügen.',
          'Ctrl+A — alles auf der Seite auswählen.',
          'Escape oder Klick außerhalb — Auswahl aufheben.',
        ],
      },
      {
        title: 'Auswahl per Touch',
        items: [
          'Einmal tippen — Datei oder Ordner öffnen.',
          'Doppeltippen auf einer Karte — zur Auswahl hinzufügen.',
          'Ziehen auf leerem Bereich (Gummiband) — Mehrfachauswahl.',
          'Langer Druck auf leerem Bereich — Kontextmenü.',
        ],
      },
      {
        title: 'Drag and Drop',
        items: [
          'Datei oder Ordner auf einen anderen Ordner ziehen, um zu verschieben.',
          'Ist das Element bereits ausgewählt, wird die gesamte Auswahl verschoben.',
          'Auf dem Smartphone zeigt Touch-Drag eine schwebende Vorschaukarte.',
        ],
        tip: 'Drag and Drop ist im Papierkorb deaktiviert.',
      },
      {
        title: 'Ausschneiden und Einfügen',
        items: [
          'Elemente auswählen → „Ausschneiden“ im Kontextmenü oder Ctrl+X.',
          'Zielordner öffnen → „Einfügen“ oder Ctrl+V.',
          'Einfügen in denselben Ordner, aus dem ausgeschnitten wurde, ist nicht möglich.',
        ],
      },
      {
        title: 'Kontextmenü',
        text: 'Rechtsklick in der Galerie (oder langer Druck per Touch) für Aktionen.',
        items: [
          'Bilder hochladen / Ordner erstellen.',
          'Ausschneiden, Einfügen.',
          'Ordner umbenennen (genau ein Ordner ausgewählt).',
          'Galerie erstellen (genau ein Ordner ausgewählt).',
          'Tag hinzufügen (nur Dateien in der Auswahl).',
          'Generieren — ausgewählte Bilder (Editoren und Admins).',
          'Löschen (verschiebt in den Papierkorb).',
          'Wiederherstellen (nur im Papierkorb).',
        ],
        tip: 'Tastenkürzel-Hinweise werden neben Menüpunkten angezeigt.',
      },
      {
        title: 'Massenaktionen',
        items: [
          'Auswahl löschen — in den Papierkorb (mit Bestätigung).',
          'Tag hinzufügen — für alle ausgewählten Dateien auf einmal.',
          'Ausschneiden und Einfügen — Gruppe von Dateien verschieben.',
        ],
      },
      {
        title: 'KI-Bildgenerierung (Galerie)',
        items: [
          'Ein oder mehrere Bilder auswählen → Kontextmenü → Generieren.',
          'Ein neues Bild pro ausgewählter Datei, nacheinander. Ergebnisse herunterladen oder speichern — Originale bleiben unverändert.',
        ],
      },
    ],
  },
  viewer: {
    title: 'Datei-Viewer',
    intro: 'Vollbildfenster mit Dateiinfos, Kommentaren und Aktionen.',
    blocks: [
      {
        title: 'So öffnen Sie Dateien',
        items: [
          'Klick auf eine Dateikarte in der Galerie (oder Name in der Listenansicht).',
          'Im Mehrfachauswahlmodus — Doppelklick / Doppeltippen.',
        ],
      },
      {
        title: 'Zwischen Dateien navigieren',
        items: [
          'Pfeile Vorherige / Nächste an den Seiten des Fensters.',
          '← und → auf der Tastatur.',
          'Escape — schließen (oder Vollbild-Bildansicht beenden).',
        ],
      },
      {
        title: 'Tab „Info“',
        items: [
          'Name, Auflösung, Größe, Upload-Datum.',
          'Tags — Klick auf einen Tag filtert die Galerie danach.',
          'Schaltflächen: Bearbeiten, Herunterladen, Öffentlichen Link erstellen/widerrufen, URL kopieren.',
        ],
        where: 'Rechtes Panel des Modals → Tab „Info“.',
      },
      {
        title: 'Tags',
        items: [
          'Tag-Symbol in der Ecke — neuen Tag hinzufügen.',
          '× auf einem Tag-Chip — Tag entfernen.',
        ],
      },
      {
        title: 'KI-Bildgenerierung',
        items: [
          'Funken-Symbol neben Tag — Hintergrund beschreiben und aus der aktuellen Datei ein neues Produktbild erzeugen.',
          'Die Vorschau bleibt im Modal: herunterladen oder als neue Datei in einen Ordner speichern (das Original wird nicht überschrieben).',
        ],
        where: 'Viewer-Leiste → Funken-Symbol (Editoren und Admins).',
      },
      {
        title: 'Öffentlicher Link für eine Datei',
        items: [
          '„Öffentlichen Link erstellen“ — jeder mit dem Link kann ohne Anmeldung ansehen.',
          '„Link widerrufen“ — Zugriff endet.',
          '„URL kopieren“ — Link in die Zwischenablage kopieren.',
        ],
        tip: 'Dies ist ein Einzeldatei-Link. Für Alben nutzen Sie Öffentliche Galerien.',
      },
      {
        title: 'Tab „Kommentare“',
        items: [
          'Kommentare und Antworten zur Datei schreiben.',
          'Eigene Kommentare bearbeiten und löschen.',
          'Emoji-Reaktionen zu Kommentaren hinzufügen.',
          'Kollegen mit @ erwähnen — Name oder E-Mail eingeben.',
        ],
        where: 'Rechtes Panel → Tab „Kommentare“. Neue Kommentar-Hinweise erscheinen in der Glocke.',
      },
      {
        title: 'Vollbildansicht',
        text: 'Klicken Sie auf das Erweitern-Symbol am Bild für die Vollbildansicht.',
      },
    ],
  },
  editor: {
    title: 'Bildeditor',
    intro: 'Öffnen Sie eine Datei aus dem Viewer mit „Bearbeiten“. Die Leinwand besteht aus Ebenen: Rahmen, Bilder, Text und Formen. Alle Änderungen können rückgängig gemacht werden.',
    blocks: [
      {
        title: 'Obere Leiste',
        items: [
          'Vergrößern / Verkleinern / An Bildschirm anpassen — Leinwand-Skalierung.',
          'Rückgängig / Wiederholen — Bearbeitungsverlauf.',
          'Zurücksetzen — Originalbild wiederherstellen.',
          'Aktueller Zoom (%) und Rahmen-Pixelmaße werden angezeigt.',
        ],
      },
      {
        title: 'Ebenen und Eigenschaften',
        items: [
          'Ebenen-Panel (links) — Liste aus Rahmen, Bildern, Text und Formen; auswählen, neu anordnen, sperren, löschen.',
          'Eigenschaften-Panel (rechts) — Einstellungen für die ausgewählte Ebene.',
          'Klick auf eine Ebene auf der Leinwand oder in der Liste zum Bearbeiten.',
          'Bild hinzufügen — zusätzliche Bildebenen aus Ihrem Workspace.',
          'Ctrl+C — ausgewählte Ebene kopieren (Bild, Sticker, Text oder Form). Der Rahmen kann nicht kopiert werden.',
          'Ctrl+V — kopierte Ebene mit leichtem Versatz einfügen.',
        ],
        where: 'Symbolleisten-Schaltflächen mit Ebenen- und Schieberegler-Symbolen.',
      },
      {
        title: 'Rahmen (Layout)',
        items: [
          'Größe, Hintergrund, Eckenradius und Rahmen.',
          'Seitenverhältnis festlegen — Vorgabe wählen (1:1, 16:9, 4:3 usw.) für den Rahmen.',
          'Bei geändertem Seitenverhältnis bleiben Text und Formen an Ort und Stelle und behalten ihre Größe; nur der sichtbare Rahmenbereich ändert sich.',
          'Die Bildebene behält ihre visuelle Position; Größe oder Position können Sie anschließend wie gewohnt anpassen.',
        ],
        where: 'Eigenschaften-Panel (rechts), wenn die Rahmen-Ebene ausgewählt ist.',
        tip: 'Escape beendet den Seitenverhältnis-Modus.',
      },
      {
        title: 'Bildebene',
        items: [
          'Bildebene auswählen — im Eigenschaften-Panel erscheint „Bild“.',
          'Position und Größe — numerische Felder; Rotation — Schieberegler (−180°…+180°) im Abschnitt Rotation.',
          'Rechtsklick auf das Bild → Größenänderungsmodus; an Ecken ziehen. Innerhalb des Bildes ziehen, um im Rahmen zu verschieben.',
          'Abschnitt Transformieren — Mit KI bearbeiten (Prompt-Leiste unten auf der Leinwand, bis 200 Zeichen), Hintergrund entfernen (nutzt den gemeinsamen KI-Token-Pool), begradigen (Winkel-Schieberegler erscheint unten; „Fertig“ klicken), horizontal / vertikal spiegeln.',
          'Abschnitt Transformieren — Zuschneiden (freies Rechteck + Schneiden), Lasso (innen löschen oder nur Auswahl behalten + Häkchen zum Anwenden).',
          'Abschnitte Farbe und Effekte — Helligkeit, Kontrast, Sättigung und Filter-Vorgaben.',
        ],
        where: 'Eigenschaften-Panel (rechts), wenn eine Bildebene ausgewählt ist.',
        tip: '„Mit KI bearbeiten“ sendet die Ebene wie angezeigt (Rotation, Spiegelung, Filter) und ersetzt sie auf der Leinwand. Escape beendet Größenänderung, Begradigen, Zuschneiden, Lasso oder die KI-Eingabe. Doppeltippen schließt den Lasso-Pfad auf Touch-Geräten.',
      },
      {
        title: 'Sticker',
        items: [
          'Die Schaltfläche „Sticker hinzufügen“ in der Symbolleiste öffnet Ihre Workspace-Sticker-Bibliothek.',
          'Ein neuer Sticker entsteht bei „Hintergrund entfernen“ auf einer Bildebene — das Ergebnis wird in der Bibliothek gespeichert und auf die aktuelle Ebene angewendet.',
          'Klick auf einen Sticker im Panel oder Ziehen auf die Leinwand fügt ihn als separate Ebene hinzu.',
          'Sticker verhalten sich wie Bilder: Position, Größe, Rotation, Schatten; sie erscheinen mit eigenem Symbol im Ebenen-Panel.',
          'Das × auf einer Bibliothekskarte entfernt den Sticker dauerhaft aus dem Workspace (Ebenen auf der Leinwand bleiben unberührt).',
        ],
        where: 'Symbolleiste → Sticker-Symbol; Sticker-Panel links (neben dem Ebenen-Panel).',
        tip: 'Hintergrund entfernen nutzt die Ebene wie angezeigt, einschließlich Rotation, Spiegelung, Skalierung und Filtern.',
      },
      {
        title: 'Textebenen',
        items: [
          'Überschrift oder Fließtext-Ebene über die Symbolleiste hinzufügen.',
          'Schriftart, Größe, Farbe, Kontur, Schatten, Hintergrund, Abstand.',
          'Ausrichtung — horizontal (links / zentriert / rechts) und vertikal (oben / zentriert / unten) im Textfeld.',
          'Text auf der Leinwand ziehen; Textfeld an Ecken in der Größe ändern.',
          '„Auf Bild anwenden“ — Text vor dem Speichern ins Bitmap einbacken.',
        ],
        where: 'Auf Mobilgeräten Eigenschaften-Panel über das Schieberegler-Symbol öffnen.',
      },
      {
        title: 'Formen',
        items: [
          'Rechteck, Dreieck, Kreis, Linie — Werkzeug wählen und auf der Leinwand ziehen zum Zeichnen.',
          'Füllung, Kontur, Eckenradius, Schatten und Verlauf in den Eigenschaften.',
          'Ziehen zum Verschieben; Ecken zum Größenändern; oberer Griff zum Drehen.',
          'Escape bricht das aktive Zeichenwerkzeug ab.',
        ],
      },
      {
        title: 'Leinwand-Navigation',
        items: [
          'Maus: ziehen zum Verschieben; Rad zum Zoomen.',
          'Touch: Pinch zum Zoomen; ein Finger zum Verschieben.',
        ],
      },
      {
        title: 'Speichern',
        items: [
          'Speichern — Originaldatei überschreiben.',
          'Herunterladen — Datei auf Ihren Computer speichern.',
          'Speichern unter — neue Datei (Name, PNG/JPG/WebP, Größe, Ordner).',
        ],
        where: 'Speichern-Schaltfläche und Pfeil-Menü rechts in der Editor-Kopfzeile.',
      },
    ],
  },
  publicGalleries: {
    title: 'Öffentliche Galerien',
    intro: 'Ansprechende Seiten, um Arbeit Kunden zu präsentieren — ohne Konto erforderlich.',
    blocks: [
      {
        title: 'Galerie aus einem Ordner',
        items: [
          'Genau einen Ordner auswählen.',
          'Kontextmenü → „Galerie erstellen“ (oder Ctrl+Alt+G).',
          'Name, Layout, Theme, Autoreninfos festlegen.',
          'Link kopieren und an Ihren Kunden senden.',
        ],
      },
      {
        title: 'Tag-Galerie',
        items: [
          'Filter nach einem oder mehreren Tags aktivieren.',
          '„Tag-Galerie erstellen“ klicken.',
          'Inhalt aktualisiert sich automatisch, wenn Sie Datei-Tags ändern.',
        ],
        where: 'Schaltfläche erscheint in der Symbolleiste, wenn der Tag-Filter aktiv ist.',
      },
      {
        title: 'Galerie-Einstellungen',
        items: [
          'Layout: Raster, Masonry oder große Karten.',
          'Theme: Hell/Dunkel und Akzentfarbe.',
          'Visitenkarte mit QR-Code generieren.',
        ],
      },
      {
        title: 'Autoren-Panel',
        items: [
          'Modus „Autor“ — Name, Tätigkeit, Kontakte und Beschreibung.',
          'Modus „Unternehmen“ — Firmenname, Logo aus Workspace-Dateien, Slogan, Kontakte und „Über uns“.',
          'Beim Wechsel des Modus bleiben die Daten des anderen Modus erhalten.',
          'Ausgefüllte Felder erscheinen in der rechten Seitenleiste der öffentlichen Seite und auf der digitalen Visitenkarte.',
        ],
        where: 'Abschnitt „Autor“ im Dialog zum Erstellen oder Bearbeiten einer Galerie.',
      },
      {
        title: 'Galerien verwalten',
        items: [
          'Seitenleiste → „Galerien“ — Katalog aller öffentlichen Galerien nach Workspace.',
          'Öffnen, bearbeiten, löschen oder QR-Visitenkarte — Aktionen auf der Karte.',
        ],
        where: 'Seitenleiste → „Galerien“ (Seite /galleries).',
      },
      {
        title: 'Was Besucher sehen',
        items: [
          'Bildraster mit unendlichem Scrollen.',
          'Vollbildansicht (Escape, ← →).',
          'Autoren-Panel mit Personen- oder Unternehmensdaten und Kontakten.',
          'Digitale Visitenkarte mit QR-Code zur Galerie herunterladen.',
        ],
      },
    ],
  },
  profile: {
    title: 'Profil und Teams',
    intro: 'Persönliche Einstellungen, Workspaces und Teammitglieder.',
    blocks: [
      {
        title: 'Profil öffnen',
        items: [
          'Eintrag „Profil“ unten in der Seitenleiste.',
          '„Abmelden“ — Sitzung beenden (Initialen-Menü, oben rechts).',
        ],
        tip: 'Das Profil öffnet sich als Vollseite; Hilfe (Fragezeichen) ist ein separates Panel.',
      },
      {
        title: 'Tab „Persönlich“',
        items: [
          'Name und E-Mail ändern.',
          '„Änderungen speichern“ klicken.',
        ],
      },
      {
        title: 'Tab „KI-Tokens“',
        items: [
          'Zeigt verbrauchte und verbleibende Tokens aus dem gemeinsamen Pool (Hintergrund entfernen, Bildgenerierung, KI-Bearbeitung).',
          'Wenn die Tokens aufgebraucht sind, sind KI-Aktionen deaktiviert, bis ein Admin Ihr Limit erhöht.',
        ],
        where: 'Profil → KI-Tokens.',
      },
      {
        title: 'Tab „Sicherheit“',
        items: [
          'Passwort ändern: neues Passwort und Bestätigung.',
          'Passwort muss Komplexitätsanforderungen erfüllen.',
        ],
      },
      {
        title: 'Tab „Workspaces“',
        items: [
          'Neuen Workspace anlegen (Name, Sprache, Theme, Akzent).',
          'Eigenen Workspace bearbeiten oder löschen.',
          'Fremden Workspace verlassen.',
          'Speichernutzung anzeigen.',
        ],
      },
      {
        title: 'Tab „Teams“',
        items: [
          'Mitgliederliste für jeden Ihrer Workspaces, mit belegt / max. Teamplätzen (Limit inkl. Eigentümer).',
          'Rolle ändern: Admin, Editor, Viewer (⋮ → Rolle bearbeiten).',
          'Sichtbarkeit festlegen: Ordner, Tabellen oder Boards vor einem Mitglied ausblenden (⋮ → Sichtbarkeit festlegen).',
          'Mitglied aus dem Workspace entfernen.',
        ],
      },
      {
        title: 'Sichtbarkeit',
        items: [
          'Sichtbarkeit ist eine Deny-Liste zusätzlich zur Rolle: eine ausgeblendete Ressource bleibt unzugänglich, auch wenn die Rolle sie erlauben würde.',
          'Der Owner kann Ressourcen vor jedem Mitglied ausblenden. Admin nur vor Editor und Viewer — nicht vor Owner oder einem anderen Admin.',
          'Das Ausblenden eines Ordners blendet auch Unterordner und Dateien in der App aus (Listen, Suche, Öffnen per URL).',
          'Tabellen und Boards werden getrennt von Ordnern ausgeblendet.',
          'Beim Einladen: nach der Rollenwahl können optional Ausblendungen gesetzt und dann die Einladung gesendet werden.',
          'Öffentliche Dateilinks funktionieren weiterhin, auch wenn die Datei in einem ausgeblendeten Ordner liegt.',
          'Der Root-Ordner des Workspace kann nicht ausgeblendet werden.',
        ],
        where: 'Profil → Teams → ⋮ beim Mitglied → Sichtbarkeit festlegen. Oder Glocke → Gesendet → Einladungsassistent.',
        tip: 'Ausblendungen gelten nur in diesem Workspace und nicht in anderen.',
      },
    ],
  },
  datasets: {
    title: 'Tabellen',
    intro: 'Tabellen im CRM-Stil in Ihren Arbeitsbereichen — Spalten, Zeilen und Inline-Bearbeitung.',
    blocks: [
      {
        title: 'Wo Sie Tabellen finden',
        items: [
          'Seitenleiste → „Daten“ → „Alle“ — Katalog aller Tabellen nach Arbeitsbereich (Seite /data).',
          'Seitenleiste → „Daten“: eigene und geteilte Tabellen.',
          '„Erstellen“ unter Daten — neue Tabelle im aktuellen Arbeitsbereich.',
          'Auf der Katalogseite — öffnen, bearbeiten, löschen, exportieren oder Schema anzeigen über die Kartenaktionen.',
        ],
        where: 'Linke Seitenleiste → Daten (und „Alle“ für den vollständigen Katalog).',
      },
      {
        title: 'Tabelle öffnen',
        items: [
          'Klick auf den Namen in der Seitenleiste oder eine Karte auf /data — CRM-Grid unter /data/:id.',
          'Beim Scrollen werden weitere Zeilen geladen (100 pro Batch).',
          'Die Scrollposition wird beim Zurückkehren zur gleichen Tabelle gespeichert.',
        ],
      },
      {
        title: 'Spalten und Schema',
        items: [
          '+ in der Kopfzeile — Spalte hinzufügen (Name, Typ, Optionen).',
          'Griff in der Kopfzeile ziehen — Spalten neu anordnen.',
          'Rechten Rand ziehen — Breite ändern; lokal gespeichert.',
          'Klick auf den Spaltennamen in der Kopfzeile — umbenennen (wenn Schema-Bearbeitung erlaubt).',
          '⋮-Menü in der Kopfzeile — Textfilter, Sortierung auf-/absteigend, Spalte beim horizontalen Scrollen fixieren, Sortierung/Filter zurücksetzen oder Spalte löschen.',
          'Katalog /data → Bearbeiten auf einer Karte — Name, Beschreibung und Spalten.',
        ],
      },
      {
        title: 'Sortierung und Filter',
        text: 'Jede Tabelle merkt sich eine Sortierung und einen Textfilter für Ihre Ansicht. Die Einstellungen werden im Browser gespeichert und beim Laden der Zeilen angewendet — einschließlich Endlos-Scroll und Tabellen-Download.',
        items: [
          '⋮ in der Spaltenkopfzeile öffnen (auf Tablet und Mobilgerät immer sichtbar).',
          'Auf- oder absteigend sortieren — ein kleiner Pfeil im Spaltentitel zeigt die Richtung.',
          'Es kann nur nach einer Spalte sortiert werden; „Sortierung zurücksetzen“ im selben Menü.',
          'Filter — Text eingeben und „Anwenden“ (oder Enter), um Zeilen zu behalten, deren Spalte den Text enthält.',
          'Bei aktivem Filter erscheint neben dem Spaltennamen ein kleines Filter-Symbol (wie beim Sortieren).',
          '„Filter zurücksetzen“ entfernt den Filter; Änderungen laden Zeilen neu vom Server.',
        ],
        where: 'Spaltenkopfzeile → ⋮-Menü.',
        tip: 'Sortierung und Filter gelten pro Tabelle auf diesem Gerät und werden beim Export aus dem Tabellenkatalog berücksichtigt.',
      },
      {
        title: 'Referenzlisten',
        text: 'Eine Referenzliste ist eine kleine „Wörterbuch-Tabelle“ im Arbeitsbereich — Status, Kategorien, SKUs und andere Werte, die Sie zentral pflegen möchten, statt sie in jede Tabelle zu kopieren.',
        items: [
          'Anlegen: Seitenleiste → „Daten“ → „Erstellen“ → „Referenzliste erstellen“ einschalten. Manuell befüllen oder direkt aus CSV/Excel importieren.',
          'Drinnen gibt es eine Spalte mit eindeutigen Werten. Wie bei einer normalen Tabelle: Zeilen im Grid hinzufügen, bearbeiten und löschen.',
          'Katalog /data → Stift bei der Liste: Name, Beschreibung, Schlüsselspalte umbenennen, Bereich „Listenwerte“.',
          'Dort auch Herunterladen — der Export berücksichtigt Filter und Sortierung aus dem Grid, falls gesetzt.',
          'Import in eine bestehende Liste: Liste öffnen → „CSV/Excel importieren“ → Spalte mit Schlüsseln wählen; Duplikate in der Datei werden übersprungen.',
          'In der Seitenleiste und auf der Katalogseite sind Listen mit dem Badge „List“ markiert.',
        ],
        where: 'Anlegen: Seitenleiste → Daten → Erstellen. Verwalten: Seitenleiste → Daten → Alle (/data).',
        tip: 'Eine Liste pro Arbeitsbereich — eine Quelle der Wahrheit für Status, Saisons, SKUs und Ähnliches.',
      },
      {
        title: 'Referenzspalte in einer Tabelle',
        text: 'Wenn eine Zelle aus einer gemeinsamen Liste wählen soll — nicht frei tippen — fügen Sie eine Spalte vom Typ Referenz hinzu und verknüpfen Sie sie mit der passenden Liste.',
        items: [
          'Auswahl (Select) — Optionen gelten nur in dieser Spalte. Gut für wenige lokale Werte.',
          'Referenz — Werte kommen aus einer Referenzliste im Arbeitsbereich. Gut, wenn dieselbe Menge in mehreren Tabellen vorkommt.',
          'Spalte hinzufügen: „+“ in der Kopfzeile oder Katalog /data → Tabelle bearbeiten → Spalte hinzufügen → Typ Referenz → Liste wählen.',
          'In der Zelle erscheint eine durchsuchbare Auswahlliste. Nach dem Speichern bleibt der Wert mit dem Listeneintrag verknüpft.',
          'Benennen Sie einen Wert in der Liste um, zeigt die Tabelle die aktuelle Bezeichnung.',
        ],
        where: 'Tabellenkopf → „+“ oder Daten → Alle → Bearbeiten auf einer Karte.',
        tip: 'Erst Liste anlegen und füllen, dann Referenzspalten in den Arbeits-Tabellen.',
      },
      {
        title: 'Listen und Dateien',
        text: 'Tabellenzeilen lassen sich mit Dateien in der Galerie verknüpfen; beim Ändern einer Referenzspalte können Tags auf Dateien automatisch gesetzt werden.',
        items: [
          'Schritt 1 — Dateiverknüpfung: Tabelle öffnen → „Dateiverknüpfung“ → Spalte für den Abgleich wählen (oft SKU oder Code) und Zuordnung zu Tags festlegen.',
          'Danach zeigt das Grid eine Spalte mit Vorschau verknüpfter Dateien; im Bildbetrachter sehen Sie zugehörige Zeilen.',
          'Schritt 2 — Dateiaktion an der Referenzspalte: beim Anlegen oder Bearbeiten „Dateiaktion“ auf „Tag hinzufügen“ oder „Tag in Liste ersetzen“ setzen.',
          'Greift, wenn die Zeile bereits verknüpfte Dateien hat und Sie die Zelle ändern — Tags werden automatisch aktualisiert (Tagname = Listenwert).',
          'Ohne zuerst eingerichtete Dateiverknüpfung laufen Tag-Aktionen nicht — zuerst Tabelle mit der Galerie verbinden.',
        ],
        where: 'Tabelle → Dateiverknüpfung; Tag-Aktion — in den Einstellungen der Referenzspalte.',
        tip: 'Typisch: SKU verbindet Zeile und Produktfotos; Statuswechsel in der Liste taggt Dateien automatisch als „Im Verkauf“ oder „Archiv“.',
      },
      {
        title: 'Tabellen zusammenführen',
        text: 'Kombinieren Sie zwei vorhandene Tabellen in eine schreibgeschützte Ansicht. Zeilen werden über Verknüpfungsspalten abgeglichen — nützlich für Lookups über Datensätze hinweg.',
        items: [
          'Seitenleiste → „Daten“ → „Zusammenführen“ — Assistent in drei Schritten.',
          'Schritt 1: Name und Arbeitsbereich für die zusammengeführte Tabelle.',
          'Schritt 2: Haupt- und zweite Tabelle wählen, dann Verknüpfungsspalten (in der zweiten Tabelle muss die Verknüpfungsspalte eindeutig sein).',
          'Schritt 3: auswählen, welche Spalten aus jeder Tabelle im Ergebnis erscheinen.',
          'Zusammengeführte Tabellen öffnen sich wie normale Grids, aber Zeilen und Schema sind nicht editierbar — nur Spaltenreihenfolge, Filter und Sortierung.',
          'In der Seitenleiste und im Katalog /data tragen zusammengeführte Tabellen ein „Merge“-Badge.',
        ],
        where: 'Linke Seitenleiste → Daten → Zusammenführen (neben Erstellen).',
        tip: 'Bereiten Sie zuerst beide Quelltabellen vor, mit einer gemeinsamen Schlüsselspalte (z. B. SKU oder ID), die in der zweiten Tabelle als eindeutig markiert ist.',
      },
      {
        title: 'Zeilen und Zellen',
        items: [
          'Zelle anklicken zum Bearbeiten; Enter speichert, Escape bricht ab.',
          'Tab / Umschalt+Tab — nächste / vorherige Zelle.',
          '„Zeile hinzufügen“ unten — neue Zeile (leerer Entwurf wird bei Klick außerhalb abgebrochen).',
          'Papierkorb in der Zeile — eine Zeile löschen.',
          'Checkboxen + ⋮ in der Kopfzeile — ausgewählte Zeilen massenhaft löschen.',
        ],
        tip: 'Änderungen erscheinen sofort; die App synchronisiert im Hintergrund mit dem Server.',
      },
      {
        title: 'Berechtigungen',
        items: [
          'Owner / Admin — Schema, Zeilen und Metadaten bearbeiten.',
          'Editor — nur Zeilen bearbeiten.',
          'Viewer — nur Lesen.',
        ],
      },
    ],
  },
  boards: {
    title: 'Boards',
    intro: 'Kanban-Boards über Ihre Tabellen — Spalten aus einer Referenzliste (Status), Karten aus Tabellenzeilen.',
    blocks: [
      {
        title: 'Boards finden',
        items: [
          'Seitenleiste → „Boards“ → „Alle“ — Katalog nach Workspace (Seite /boards).',
          'Seitenleiste → „Boards“: Schnellliste eigener und geteilter Boards.',
          '„Erstellen“ unter „Boards“ — neues Board im aktuellen Workspace.',
          'Im Katalog — öffnen, Einstellungen bearbeiten oder über das Kartenmenü löschen.',
        ],
        where: 'Linke Seitenleiste → Boards (und „Alle“ für den vollständigen Katalog).',
      },
      {
        title: 'Board erstellen',
        items: [
          'Schritt 1: Name, optionale Beschreibung und Workspace.',
          'Schritt 2: passende Tabelle und Referenzspalte für Status; wählen Sie Statuswerte als Spalten (mindestens einen).',
          'Schritt 3: Titelfeld und optionale Zusatzfelder auf Karten.',
          'Nach dem Erstellen öffnet sich die Board-Seite /boards/:id.',
        ],
        tip: 'Nur normale Tabellen mit Referenzspalte. Merge- und Listen-Datasets erscheinen nicht.',
      },
      {
        title: 'Kanban-Board',
        items: [
          'Spalten folgen der Statusreihenfolge; Ziehen am Griff sortiert Spalten neu (Admin).',
          'Karten zeigen Titel und einige Vorschaufelder.',
          'Ein Nachrichten-Icon mit Zähler erscheint auf der Karte, wenn Kommentare vorhanden sind.',
          'Ein Büroklammer-Icon mit Zähler erscheint, wenn Dateien angehängt sind.',
          'Karten zwischen Spalten oder innerhalb einer Spalte ziehen — Status und Reihenfolge ändern sich.',
          'Live-Updates per SSE — andere Nutzer sehen Züge, Bearbeitungen, Kommentare sowie Anhängen/Lösen von Dateien ohne Neuladen.',
        ],
      },
      {
        title: 'Karten',
        items: [
          'Karte anklicken — Ansichtsmodal mit Feldern, Tab „Kommentare“ und Tab „Dateien“.',
          '⋮ → Bearbeiten — Seitenpanel für die Tabellenzeile.',
          '⋮ → Löschen — entfernt die Zeile nach Bestätigung.',
          '„Karte hinzufügen“ unter einer Spalte — neue Zeile mit diesem Status.',
        ],
        where: 'Board-Seite → Karte oder Spaltenfuß.',
        tip: 'Deep-Link /boards/:id?card=:rowId öffnet dasselbe Kartenmodal (z. B. aus der Glocke).',
      },
      {
        title: 'Kartenkommentare',
        items: [
          'Kommentare gehören zu diesem Board + dieser Karte (Zeile), nicht zu allen Boards derselben Tabellenzeile.',
          'Alle mit Workspace-Zugang (Viewer+) können kommentieren; Autor oder Admin können fremde Kommentare löschen.',
          'Neue Kommentare benachrichtigen Workspace-Mitglieder in der Glocke.',
        ],
      },
      {
        title: 'Kartendateien',
        items: [
          'Tab „Dateien“ — Bilder aus dem Board-Workspace anhängen (derselbe Picker wie im Bildeditor).',
          'Anhänge gehören zu diesem Board + dieser Karte (Zeile), nicht zu allen Boards derselben Tabellenzeile.',
          'Klick auf die Vorschau öffnet den Bildbetrachter; Hover zeigt den Dateinamen; ✕ löst die Datei, ohne sie aus der Galerie zu löschen.',
          'Liegt die Datei im Papierkorb, bleibt der Anhang mit Overlay „im Papierkorb“; endgültiges Löschen entfernt den Anhang.',
          'Alle mit Workspace-Zugang (Viewer+) können anhängen und lösen.',
        ],
        where: 'Kartenmodal → Tab „Dateien“.',
      },
      {
        title: 'Spalten',
        items: [
          'Admin: Spalte hinzufügen (neuer oder bestehender Status), umbenennen, sortieren oder entfernen.',
          'Eine Spalte mit Karten kann nicht entfernt werden — Karten zuerst verschieben oder löschen.',
          'Entfernen blendet die Spalte nur auf diesem Board aus; der Listenwert bleibt erhalten.',
        ],
      },
      {
        title: 'Bearbeiter und Fristen',
        items: [
          'Karte öffnen → Bearbeiter: Workspace-Mitglieder hinzufügen — jeder mit eigener Frist und Zielspalte.',
          'Nur Owner / Admin können zuweisen, Fristen/Ziele ändern oder Priorität setzen (von niedrigster bis höchster).',
          'Editor sieht alle Bearbeiter; Viewer nur die eigene Zuweisung auf der Karte.',
          'Ziehen in die Zielspalte eines Bearbeiters schließt dessen Aufgabe; Zurückziehen öffnet sie wieder.',
          'Mehrere Personen mit derselben Zielspalte werden mit einem Zug alle abgeschlossen.',
          'Überfällige Zuweisungen sind auf der Karte markiert; Hinweise kommen in die Glocke bei Login/Token-Refresh (kein Hintergrund-Cron).',
          'Persönliche Hinweise zu Ihren Fristen (bald / heute / überfällig). Owner/Admin erhalten zusätzlich einen Digest, wenn jemand anderes überfällig ist.',
        ],
        where: 'Board → Kartenmodal → Bearbeiter (und Priorität unter den Feldern).',
        tip: 'Ändern der Frist setzt den Alert-Status zurück — der nächste Login/Refresh kann erneut benachrichtigen.',
      },
      {
        title: 'Berechtigungen',
        items: [
          'Viewer — Board ansehen, Karten ziehen, kommentieren und Dateien anhängen/lösen; sieht nur eigene Bearbeiter.',
          'Editor — Karten (Zeilen) erstellen, bearbeiten und löschen; sieht alle Bearbeiter, kann aber nicht zuweisen.',
          'Owner / Admin — Board-Einstellungen, Spalten, Board löschen, Kommentare moderieren, Zuweisungen, Fristen/Ziele und Priorität.',
        ],
      },
    ],
  },
  workflows: {
    title: 'Workflows',
    intro: 'Workflows sind eine eigene Produktschicht neben Dateien, Daten, Boards und Galerien — keine Einstellungsseite. Sie bauen einen Automationsgraphen, der Dateien, Zeilen und Karten durch bereits eingerichtete Systeme bewegt.',
    blocks: [
      {
        title: 'Keine Einstellungen',
        items: [
          'Workflows leben in einer eigenen Sidebar-Sektion — wie Daten oder Boards, nicht unter Profil / Einstellungen.',
          'Jeder Prozess gehört zu einem Workspace und nutzt dessen Ordner, Tabellen, Boards, Muster und Tags.',
          'Aktivieren schaltet den Prozess ein; Speichern schreibt den Graphen. Läufe zeigen, was wirklich passiert ist.',
        ],
        tip: 'Sprache, Theme und Konto finden Sie unter Profil — nicht unter Workflows.',
      },
      {
        title: 'Wo finde ich Workflows?',
        items: [
          'Seitenleiste → „Workflows“ → „Alle“ — Katalog nach Workspace (/workflows).',
          'Seitenleiste → „Workflows“ → „Aktuell“ — Kurzliste des aktiven Space (verborgen, wenn leer).',
          '„Erstellen“ unter Workflows — neuer Graph (nur Owner/Admin).',
          'Prozess öffnen — Graph-Builder; Läufe — Ausführungsjournal.',
        ],
        where: 'Linke Seitenleiste → Workflows.',
      },
      {
        title: 'Graph-Builder',
        items: [
          'Palette — Gruppen: Trigger, Dateien, Inhalt, AI, Tabelle, Board, Flow; plus Vorlagen-Panel.',
          'Canvas — Knoten platzieren, Kanten verbinden, verschieben; Rückgängig / Wiederholen neben dem Titel (auch Strg+Z / Strg+Y).',
          'Inspektor — ausgewählten Trigger oder Knoten konfigurieren (Ordner, Tags, Tabelle, Cron usw.).',
          'Genau ein Trigger. Meist eine Kette; If / Verzweigung kann True- und False-Ausgänge haben.',
          '„Prüfen“ kontrolliert Pflichtfelder und den Stream (Dateien / Zeilen) vor dem Speichern.',
          'Tastatur: Knoten kopieren / einfügen (Strg+C / Strg+V), Auswahl löschen (Entf / Backspace) — siehe Tastenkürzel.',
        ],
        tip: 'Cron: einmal, täglich, wöchentlich (Wochentag + Zeit) oder monatlich (Tag + Zeit), mit Zeitzone.',
      },
      {
        title: 'Palette: Trigger',
        text: 'Genau ein Trigger pro Prozess.',
        items: [
          'Datei hochgeladen — startet bei Upload in den überwachten Ordner.',
          'Zeile hinzugefügt — startet bei neuer Tabellenzeile.',
          'Zeilen importiert — startet nach CSV/Excel-Import.',
          'Cell matched — startet, wenn eine Zelle den gewünschten Wert erhält.',
          'File moved — startet, wenn eine Datei in den überwachten Ordner verschoben wird.',
          'On schedule — startet per Cron (Lauf beginnt leer).',
        ],
      },
      {
        title: 'Palette: Dateien',
        items: [
          'Tags setzen — fügt Tags zu Dateien im Flow hinzu.',
          'Tags entfernen — entfernt Tags von Dateien im Flow.',
          'Datei verschieben — verschiebt Dateien in den Zielordner.',
          'In Papierkorb — legt Dateien in den Papierkorb.',
          'Linked rows — findet Tabellenzeilen, die mit Dateien im Flow verknüpft sind.',
        ],
      },
      {
        title: 'Palette: Inhalt',
        items: [
          'Apply pattern — wendet ein Workspace-Muster auf jede Datei-Szene an.',
          'Save as — speichert eine Kopie jeder Datei im Zielordner.',
          'Replace content — ersetzt jede Datei durch den Freisteller von Remove background.',
        ],
      },
      {
        title: 'Palette: AI',
        items: [
          'Remove background — entfernt den Hintergrund jedes Bildes (AI-Tokens).',
          'Generate image — ein neues Bild pro Datei im Flow.',
        ],
        tip: 'AI-Knoten nutzen den gemeinsamen AI-Token-Pool im Profil.',
      },
      {
        title: 'Palette: Tabelle',
        items: [
          'Zellen setzen — schreibt Werte in gewählte Spalten der Zeilen im Flow.',
          'Insert row — fügt eine neue Tabellenzeile hinzu.',
          'Linked files — lädt verknüpfte Dateien in den Datei-Stream.',
        ],
      },
      {
        title: 'Palette: Board',
        items: [
          'Add priority — setzt Priorität auf Karten im Flow.',
          'Clear priority — entfernt Priorität von Karten im Flow.',
          'Zuweisung hinzufügen — weist ein Mitglied (mit Deadline/Spalte) zu.',
          'Unassign — entfernt Zuweisungen von Karten im Flow.',
        ],
      },
      {
        title: 'Palette: Flow',
        items: [
          'Table source — liefert Tabellenzeilen an Generate image (Data-Eingang).',
          'Search — lädt Workspace-Dateien per Tag, Name oder Endung in den Flow.',
          'Filter — behält nur passende Elemente im Flow.',
          'Link — matched Dateien mit Wörterbuchwerten nach Name (optional Tag).',
          'Limit — behält nur die ersten N Elemente.',
          'If / branch — leitet den Lauf nach True/False, ohne den Stream zu ändern.',
        ],
      },
      {
        title: 'Wer darf was (read-only für Editoren)',
        items: [
          'Owner / Admin — erstellen, bearbeiten, speichern, ein-/ausschalten, löschen und Vorlagen verwalten.',
          'Editor / Viewer — Katalog und Graph nur lesen (keine Palette, kein Speichern), plus Läufe öffnen.',
        ],
        tip: 'Editoren können Aufbau und Läufe studieren — sie ändern die Automation nicht.',
      },
      {
        title: 'Läufe / Journal',
        items: [
          'Jeder Lauf protokolliert Trigger und jeden Knotenschritt: ok, skipped oder failed, mit Dauer und Details.',
          'Lauf öffnen — Graph mit Schritt-Badges und Schrittliste.',
          'Läufe sind für jedes Workspace-Mitglied sichtbar.',
        ],
        where: 'Prozess → Läufe oder Katalogkarte → Runs.',
      },
      {
        title: 'Benutzervorlagen',
        items: [
          'Owner / Admin: Speichern-Menü → „Als Vorlage speichern“ — aktueller Graph in die Workspace-Bibliothek.',
          'Vorlagen-Panel (links) — Vorlage auf die Canvas anwenden (frische Kopie mit neuen IDs) oder löschen.',
          'Anwenden hält keinen Live-Link — Sie bearbeiten die Kopie wie jeden anderen Prozess.',
        ],
        tip: 'Vorlagen gelten pro Workspace. Editoren können sie weder anlegen noch anwenden.',
      },
    ],
  },
  notifications: {
    title: 'Benachrichtigungen und Einladungen',
    intro: 'Die Glocke in der oberen Leiste ist Ihr Posteingang für Team- und Kommentar-Ereignisse.',
    blocks: [
      {
        title: 'Tab „Kommentare“',
        items: [
          'Hinweise zu neuen Kommentaren und Antworten auf Ihre Dateien.',
          'Hinweise zu neuen Kommentaren auf Board-Karten.',
          'Fristen: bald fällig (24 Std.), heute, überfällig — wenn Sie Bearbeiter sind.',
          'Digest für Owner/Admin: jemand anderes auf Ihren Boards ist überfällig (ca. einmal täglich pro Zuweisung).',
          'Klick auf eine Datei-Benachrichtigung — öffnet das Bild und den Tab „Kommentare“.',
          'Klick auf eine Karten- oder Fristen-Benachrichtigung — öffnet /boards/:id?card=:rowId mit dem Kartenmodal.',
          '„Alle löschen“ — alle Benachrichtigungen für den aktuellen Workspace entfernen.',
        ],
        where: 'Glocke → Tab mit Nachrichten-Symbol (standardmäßig geöffnet).',
      },
      {
        title: 'Tab „Eingehend“',
        items: [
          'Einladungen, einem fremden Workspace beizutreten.',
          'Annehmen oder ablehnen.',
          'Angezeigt: wer eingeladen hat, Workspace-Name, Rolle, Ablaufdatum.',
        ],
      },
      {
        title: 'Tab „Gesendet“',
        items: [
          'Von Ihnen gesendete Einladungen.',
          'Ausstehende Einladung stornieren.',
          '„Mitglied hinzufügen“ — neuen Benutzer einladen.',
        ],
      },
      {
        title: 'So laden Sie einen Benutzer ein',
        items: [
          'Glocke → Gesendet → „Mitglied hinzufügen“.',
          'Schritt 1: Benutzer per E-Mail finden.',
          'Schritt 2: Workspace und Rolle wählen (Admin / Editor / Viewer). Ausstehende Einladungen belegen ebenfalls einen Platz.',
          'Ist das Team voll, ist Einladen deaktiviert, bis ein Systemadmin Ihr Mitgliederlimit erhöht.',
          'Schritt 3: optional Ordner, Tabellen oder Boards ausblenden, dann Einladung senden.',
        ],
      },
      {
        title: 'Badge an der Glocke',
        text: 'Das rote Badge zeigt die Summe ausstehender Einladungen und Kommentar-Hinweise für den aktuellen Workspace.',
      },
    ],
  },
  trash: {
    title: 'Papierkorb',
    intro: 'Gelöschte Dateien und Ordner landen hier, statt sofort zu verschwinden.',
    blocks: [
      {
        title: 'So öffnen Sie den Papierkorb',
        items: [
          '„Papierkorb“ in der Symbolleiste auf dem Startbildschirm klicken.',
          'Zurück-Schaltfläche in der Papierkorb-Kopfzeile — zurück zur Galerie.',
        ],
        where: 'Papierkorb-Symbol rechts in der Symbolleiste.',
      },
      {
        title: 'Wiederherstellen',
        items: [
          'Elemente auswählen → Kontextmenü → „Wiederherstellen“.',
          'Tastenkürzel: Ctrl+Alt+R.',
          'Elemente kehren an ihre ursprüngliche Position zurück.',
        ],
      },
      {
        title: 'Endgültig löschen',
        items: [
          '× auf einer Karte — eine Datei oder einen Ordner dauerhaft löschen (nur Owner).',
          '„Papierkorb leeren“ — alles löschen (mit Bestätigung, nur Owner).',
        ],
        tip: 'Nach dem Leeren des Papierkorbs können Dateien nicht wiederhergestellt werden.',
      },
      {
        title: 'Einschränkungen im Papierkorb',
        items: [
          'Kein Upload von Dateien oder Anlegen von Ordnern.',
          'Kein Drag and Drop.',
          'Sortierung nicht verfügbar.',
        ],
      },
    ],
  },
  shortcuts: {
    title: 'Tastenkürzel und Gesten',
    intro: 'Kurzreferenz. Shift+? öffnet diese Übersicht jederzeit. Tastenkürzel sind deaktiviert, wenn der Editor geöffnet ist oder der Fokus in einem Eingabefeld liegt.',
    blocks: [
      {
        title: 'Galerie',
        items: [
          'Ctrl+A — alles auswählen.',
          'Ctrl+U — Dateien hochladen.',
          'Ctrl+Shift+V — Screenshot aus der Zwischenablage einfügen.',
          'Ctrl+Alt+N — Ordner erstellen.',
          'Ctrl+X — ausschneiden.',
          'Ctrl+V — einfügen.',
          'F2 — Ordner umbenennen (einer ausgewählt).',
          'Ctrl+Alt+G — Galerie erstellen (ein Ordner).',
          'Ctrl+Alt+T — Tag hinzufügen.',
          'Delete — in den Papierkorb verschieben.',
          'Ctrl+Alt+R — wiederherstellen (im Papierkorb).',
          'Escape — Auswahl aufheben.',
        ],
      },
      {
        title: 'Datei-Viewer',
        items: [
          '← / → — vorherige / nächste Datei.',
          'Escape — Fenster schließen.',
        ],
      },
      {
        title: 'Editor',
        items: [
          'Ctrl+Z — rückgängig.',
          'Ctrl+Y — wiederholen.',
          'Ctrl+0 — an Bildschirm anpassen.',
          'Ctrl++ — vergrößern.',
          'Ctrl+- — verkleinern.',
          'H — horizontal spiegeln (ausgewähltes Bild).',
          'V — vertikal spiegeln (ausgewähltes Bild).',
          'T — Begradigungsmodus.',
          'C — freies Zuschneiden.',
          'L — Lasso innen löschen.',
          'Shift+L — Lasso nur Auswahl behalten.',
          'Ctrl+C — ausgewählte Ebene kopieren (nicht den Rahmen).',
          'Ctrl+V — Ebene einfügen.',
          'Delete / Backspace — ausgewählte Ebene löschen (Text, Bild oder Form).',
          'Escape — aktuellen Modus beenden (Seitenverhältnis, Größenänderung, Formwerkzeug, Zuschneiden, Lasso) oder Änderungen zurücksetzen.',
        ],
        tip: 'Tastenkürzel-Hinweise erscheinen in Editor-Schaltflächen-Tooltips (am Desktop mit Maus). Seitenverhältnis des Rahmens und Ebenen-Werkzeuge haben keine Buchstaben-Kürzel — nutzen Sie das Eigenschaften-Panel.',
      },
      {
        title: 'Tabellen (CRM-Grid)',
        items: [
          'Enter — Zelle speichern oder Bearbeitung starten.',
          'Escape — Zellenbearbeitung abbrechen.',
          'Tab / Umschalt+Tab — nächste / vorherige Zelle.',
        ],
        where: 'Beliebige Tabelle aus der Seitenleiste öffnen.',
      },
      {
        title: 'Prozess-Editor (Workflows)',
        items: [
          'Strg+Z — rückgängig.',
          'Strg+Y oder Strg+Umschalt+Z — wiederholen.',
          'Strg+C — ausgewählte Knoten kopieren.',
          'Strg+V — Knoten einfügen.',
          'Entf / Backspace — ausgewählte Knoten löschen.',
        ],
        where: 'Prozess über die Seitenleiste öffnen (Workflows).',
        tip: 'Kürzel nutzen physische Tasten (auch bei nicht-lateinischer Belegung). Deaktiviert bei Fokus im Eingabefeld oder bei schreibgeschütztem Prozess.',
      },
      {
        title: 'Touch-Gesten',
        items: [
          'Einmal tippen — öffnen; doppelt tippen — auswählen.',
          'Gummiband — Mehrfachauswahl.',
          'Langer Druck — Kontextmenü.',
          'Pinch im Editor — zoomen.',
        ],
      },
    ],
  },
};
